<script lang="ts">
  import { derived } from "svelte/store";
  import { EdgeStatus, type NodesWithStories } from "@client";
  import { ArrowDownIcon } from "@rgossiaux/svelte-heroicons/solid";
  import Pastille from "@components/icons/Pastille.svelte";
  import { nodeTypes } from "@utils/nodeTypes";
  import Carousel from "svelte-carousel";
  import DeleteButton from "../NodeEditor/edit/DeleteButton.svelte";
  import Can from "@components/AccessControl/Can.svelte";
  import { Roles } from "@utils/decorators";
  import DialogConfirmAction from "@components/dialog/DialogConfirmAction.svelte";
  import { PhotographIcon } from "@rgossiaux/svelte-heroicons/outline";
  import {
    CheckIcon,
    XIcon,
    QuestionMarkCircleIcon,
  } from "@rgossiaux/svelte-heroicons/solid";
  import { dataStore } from "@stores/datastore";
  import { ResourceType } from "../../../client/models/ResourceType";
  const { _selectedEdge } = dataStore;
  import { getCtx as getResourceCtx } from "@components/Actions/CreateResource/context";
  import { cycleEdgeStatus, deleteEdge, getEdgeStatusName, updateEdge } from "@utils/edge";

  let { resources } = getResourceCtx();
  let dialogDeleteConnection;

  function onDelete() {
    deleteEdge($_selectedEdge.id);
  }

  const handleNodeClick = (node: NodesWithStories) => {
    dataStore.setSelectedNode(node);
    dataStore.selectionHistoryAppend(node);
  }

  let statusButtonHover = false;
  const handleStatusButtonMouseEnter = () => {
    statusButtonHover = true;
  }

  const handleStatusButtonMouseLeave = () => {
    statusButtonHover = false;
  }

  let displayedStatus = derived(_selectedEdge, ($_selectedEdge) => {
    return ($_selectedEdge?.status) ? getEdgeStatusName($_selectedEdge.status) : "";
  });
  let nextDisplayedStatus = derived(_selectedEdge, ($_selectedEdge) => {
    return ($_selectedEdge?.status) ? getEdgeStatusName(cycleEdgeStatus($_selectedEdge)) : "";
  });
</script>

{#if $_selectedEdge}
  <div class="flex items-center justify-between my-2 mr-2">
    <div class="flex-none text-opacity-50 uppercase text-md text-base-content">
      Connection
    </div>
    <div class="flex-none">
      <Can roles={[Roles.STORY, Roles.DEV]}>
        <DeleteButton confirmDialog={dialogDeleteConnection}>
          <DialogConfirmAction
            bind:this={dialogDeleteConnection}
            type="deleteConnection"
            actionOnYes={onDelete}
          />
        </DeleteButton>
      </Can>
    </div>
  </div>
  <div class="flex flex-row">
    <div>
      <Pastille type={$_selectedEdge.source.type} />
      <span class="capitalize">{$_selectedEdge.source.type}</span>
      <div>
        <span
          class="navigate"
          on:click={() => handleNodeClick($_selectedEdge.source)}
          >{$_selectedEdge.source.name}</span
        >
      </div>
    </div>
  </div>
  <div>
    <ArrowDownIcon class="w-4 h-4" />
  </div>
  <div class="text-xl font-bold">
    {nodeTypes[$_selectedEdge.source.type].sectionto[
      nodeTypes[$_selectedEdge.source.type].to.indexOf(
        $_selectedEdge.target.type
      )
    ]}
  </div>
  <div>
    <ArrowDownIcon class="w-4 h-4" />
  </div>
  <div>
    <Pastille type={$_selectedEdge.target.type} />
    <span class="capitalize">{$_selectedEdge.target.type}</span>
  </div>
  <div>
    <span
      class="navigate"
      on:click={() => handleNodeClick($_selectedEdge.target)}
      >{$_selectedEdge.target.name}</span
    >
  </div>
  <div class="flex items-center justify-start mt-1 mb-1 text-xs">
    {#if $_selectedEdge.status === EdgeStatus.TO_CHECK}
      <QuestionMarkCircleIcon class="w-4 h-4 mr-2 rounded-full btn-warning" />
    {:else if $_selectedEdge.status === EdgeStatus.VALIDATED}
      <CheckIcon class="w-4 h-4 mr-2 rounded-full btn-success" />
    {:else if $_selectedEdge.status === EdgeStatus.REJECTED}
      <XIcon class="w-4 h-4 mr-2 rounded-full btn-error" />
    {/if}
    <span>
      Created by: {$_selectedEdge.inserted_by}
    </span>
    <button
      type="button"
      class="ml-2 kg-btn-outline"
      style="padding: 6px; height: 1.6rem; min-height: 1.6rem;"
      disabled={!$_selectedEdge.source.validated || !$_selectedEdge.target.validated}
      on:click={() => updateEdge($_selectedEdge.id, { status: cycleEdgeStatus($_selectedEdge) })}
      on:mouseenter={handleStatusButtonMouseEnter}
      on:mouseleave={handleStatusButtonMouseLeave}
    >
      {$displayedStatus}{statusButtonHover && $nextDisplayedStatus ? ` ➜ ${$nextDisplayedStatus}` : ""}
    </button>
  </div>
  <div>
    {#await resources.init("edge", $_selectedEdge.id) then}
      {#if $resources && $resources.filter((r) => r.type === ResourceType.PHOTO && r.soft_deleted_by === null).length !== 0}
        <Carousel>
          {#each $resources.filter((r) => r.type === ResourceType.PHOTO && r.soft_deleted_by === null) as resource}
            <img
              src={resource.url}
              class="object-cover h-full col-span-2 rounded-r-lg"
              alt={resource.description}
            />
          {/each}
        </Carousel>
      {:else}
        <div
          class="flex flex-col items-center justify-center mr-4 rounded-lg bg-base-300 h-28"
        >
          <PhotographIcon class="opacity-50 w-9 h-9 text-base-content" />
          <span class="opacity-50 text-base-content"
            >There is no picture at the moment</span
          >
        </div>
      {/if}
    {/await}
  </div>
{/if}

<style>
  .navigate {
    @apply cursor-pointer;
  }
</style>
